import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  // Devices & Hardware
  Laptop, Monitor, Smartphone, Tablet, Keyboard, Mouse, Headphones, Speaker, Cpu, HardDrive,
  Printer, Usb, Wifi, Bluetooth, Cable, Router,
  // Office & Work
  Package, Briefcase, Building2, DoorOpen, CreditCard, Key, KeyRound, Lock, IdCard,
  // Tools
  Wrench, Settings, Hammer, Plug, Zap,
  // Communication & Cloud
  Mail, MessageSquare, Phone, Video, Globe, Cloud, Server, Database, Shield,
  // Software & Access
  AppWindow, Code, FileCode, Terminal, GitBranch, Github, PenTool, ListTodo, Figma, Slack,
  // Services & Platforms
  CircleDot, Blocks, Container, Layers, LayoutGrid, Puzzle, Rocket, Compass,
  FileSpreadsheet, Kanban, BookOpen, GraduationCap, Users, UserCog,
  Camera, Image, Music, Palette, Scissors,
  // Transport
  Car, Bike,
  // Other
  Box, Archive, Tag, Bookmark, Flag, Star, Heart, Search,
  ChevronDown,
} from 'lucide-react';

export interface IconOption {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  category: string;
}

const ICON_OPTIONS: IconOption[] = [
  // Devices & Hardware
  { name: 'laptop', icon: Laptop, category: 'Devices' },
  { name: 'monitor', icon: Monitor, category: 'Devices' },
  { name: 'smartphone', icon: Smartphone, category: 'Devices' },
  { name: 'tablet', icon: Tablet, category: 'Devices' },
  { name: 'keyboard', icon: Keyboard, category: 'Devices' },
  { name: 'mouse', icon: Mouse, category: 'Devices' },
  { name: 'headphones', icon: Headphones, category: 'Devices' },
  { name: 'speaker', icon: Speaker, category: 'Devices' },
  { name: 'cpu', icon: Cpu, category: 'Devices' },
  { name: 'hard-drive', icon: HardDrive, category: 'Devices' },
  { name: 'printer', icon: Printer, category: 'Devices' },
  { name: 'usb', icon: Usb, category: 'Devices' },
  { name: 'cable', icon: Cable, category: 'Devices' },
  { name: 'router', icon: Router, category: 'Devices' },

  // Connectivity
  { name: 'wifi', icon: Wifi, category: 'Connectivity' },
  { name: 'bluetooth', icon: Bluetooth, category: 'Connectivity' },
  { name: 'globe', icon: Globe, category: 'Connectivity' },
  { name: 'cloud', icon: Cloud, category: 'Connectivity' },
  { name: 'server', icon: Server, category: 'Connectivity' },
  { name: 'database', icon: Database, category: 'Connectivity' },

  // Office & Access
  { name: 'briefcase', icon: Briefcase, category: 'Office' },
  { name: 'building-2', icon: Building2, category: 'Office' },
  { name: 'door-open', icon: DoorOpen, category: 'Office' },
  { name: 'credit-card', icon: CreditCard, category: 'Office' },
  { name: 'id-card', icon: IdCard, category: 'Office' },
  { name: 'key', icon: Key, category: 'Office' },
  { name: 'key-round', icon: KeyRound, category: 'Office' },
  { name: 'lock', icon: Lock, category: 'Office' },
  { name: 'shield', icon: Shield, category: 'Office' },

  // Software
  { name: 'app-window', icon: AppWindow, category: 'Software' },
  { name: 'code', icon: Code, category: 'Software' },
  { name: 'file-code', icon: FileCode, category: 'Software' },
  { name: 'terminal', icon: Terminal, category: 'Software' },
  { name: 'git-branch', icon: GitBranch, category: 'Software' },
  { name: 'github', icon: Github, category: 'Software' },
  { name: 'figma', icon: Figma, category: 'Software' },
  { name: 'slack', icon: Slack, category: 'Software' },
  { name: 'pen-tool', icon: PenTool, category: 'Software' },
  { name: 'list-todo', icon: ListTodo, category: 'Software' },

  // Services & Platforms (for SaaS tools like Azure, AWS, Notion, etc.)
  { name: 'circle-dot', icon: CircleDot, category: 'Services' },
  { name: 'blocks', icon: Blocks, category: 'Services' },
  { name: 'container', icon: Container, category: 'Services' },
  { name: 'layers', icon: Layers, category: 'Services' },
  { name: 'layout-grid', icon: LayoutGrid, category: 'Services' },
  { name: 'puzzle', icon: Puzzle, category: 'Services' },
  { name: 'rocket', icon: Rocket, category: 'Services' },
  { name: 'compass', icon: Compass, category: 'Services' },
  { name: 'file-spreadsheet', icon: FileSpreadsheet, category: 'Services' },
  { name: 'kanban', icon: Kanban, category: 'Services' },
  { name: 'book-open', icon: BookOpen, category: 'Services' },
  { name: 'graduation-cap', icon: GraduationCap, category: 'Services' },
  { name: 'users', icon: Users, category: 'Services' },
  { name: 'user-cog', icon: UserCog, category: 'Services' },

  // Creative
  { name: 'camera', icon: Camera, category: 'Creative' },
  { name: 'image', icon: Image, category: 'Creative' },
  { name: 'music', icon: Music, category: 'Creative' },
  { name: 'palette', icon: Palette, category: 'Creative' },
  { name: 'scissors', icon: Scissors, category: 'Creative' },

  // Communication
  { name: 'mail', icon: Mail, category: 'Communication' },
  { name: 'message-square', icon: MessageSquare, category: 'Communication' },
  { name: 'phone', icon: Phone, category: 'Communication' },
  { name: 'video', icon: Video, category: 'Communication' },

  // Tools
  { name: 'wrench', icon: Wrench, category: 'Tools' },
  { name: 'settings', icon: Settings, category: 'Tools' },
  { name: 'hammer', icon: Hammer, category: 'Tools' },
  { name: 'plug', icon: Plug, category: 'Tools' },
  { name: 'zap', icon: Zap, category: 'Tools' },

  // Transport
  { name: 'car', icon: Car, category: 'Transport' },
  { name: 'bike', icon: Bike, category: 'Transport' },

  // Other
  { name: 'package', icon: Package, category: 'Other' },
  { name: 'box', icon: Box, category: 'Other' },
  { name: 'archive', icon: Archive, category: 'Other' },
  { name: 'tag', icon: Tag, category: 'Other' },
  { name: 'bookmark', icon: Bookmark, category: 'Other' },
  { name: 'flag', icon: Flag, category: 'Other' },
  { name: 'star', icon: Star, category: 'Other' },
  { name: 'heart', icon: Heart, category: 'Other' },
];

// Build lookup map for resolving icon by name
const ICON_MAP = new Map(ICON_OPTIONS.map((opt) => [opt.name, opt.icon]));

export function getIconByName(name?: string | null): React.ComponentType<{ className?: string }> {
  return (name && ICON_MAP.get(name)) || Package;
}

interface IconPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function IconPicker({ value, onChange, disabled }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const SelectedIcon = getIconByName(value);

  const filtered = useMemo(() => {
    if (!search.trim()) return ICON_OPTIONS;
    const q = search.toLowerCase();
    return ICON_OPTIONS.filter(
      (opt) => opt.name.includes(q) || opt.category.toLowerCase().includes(q),
    );
  }, [search]);

  const grouped = useMemo(() => {
    const map = new Map<string, IconOption[]>();
    for (const opt of filtered) {
      const list = map.get(opt.category) || [];
      list.push(opt);
      map.set(opt.category, list);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-full justify-between font-normal"
        >
          <div className="flex items-center gap-2">
            <SelectedIcon className="h-4 w-4 text-muted-foreground" />
            <span className={cn(!value && 'text-muted-foreground')}>
              {value || 'Select icon...'}
            </span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-2 space-y-3">
          {grouped.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-4">No icons found</p>
          ) : (
            grouped.map(([category, icons]) => (
              <div key={category}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  {category}
                </p>
                <div className="grid grid-cols-6 gap-1">
                  {icons.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = value === opt.name;
                    return (
                      <button
                        key={opt.name}
                        type="button"
                        title={opt.name}
                        onClick={() => {
                          onChange(opt.name);
                          setOpen(false);
                          setSearch('');
                        }}
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-md transition-colors cursor-pointer',
                          isSelected
                            ? 'bg-hygge/15 text-hygge ring-1 ring-hygge/30'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
